import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PiCaretDown, PiCaretUp, PiUserPlus, PiUsers } from 'react-icons/pi';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { useUsers } from 'src/modules/shared/hooks/api/admin/users';
import { PaginationBar } from 'src/modules/shared/components/molecules/paginations/pagination-bar';
import Pagination from 'src/modules/shared/components/molecules/paginations/pagination';
import { useTeams } from 'hooks/api/teams';
import { UsersTable } from '../organisms/users-table';
import { InviteUserModal } from '../molecules/invite-user-modal';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { UserFilters } from '../molecules/user-filters';
import type { UserFiltersState } from '../../types/user-filters.types';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { UserPermission } from 'src/modules/shared/types/permissions.type';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';

const UsersPage: React.FC = () => {
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [filters, setFilters] = useState<UserFiltersState>({});
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [isToolbarExpanded, setIsToolbarExpanded] = useState<boolean>(true);
  const { t } = useI18n();

  const { hasPermission: canCreateUser } = useHasPermissions(
    UserPermission.Create
  );

  const query = useMemo(() => {
    const queryBuilder = createQuery()
      .orderByDesc('createdAt')
      .setPage(page)
      .take(pageSize);

    if (filters.status === 'active') {
      queryBuilder.equals('_include', 'active');
    } else if (filters.status === 'inactive') {
      queryBuilder.equals('_include', 'inactive');
    } else {
      queryBuilder.equals('_include', 'all');
    }

    if (filters.profile && filters.profile !== 'all') {
      queryBuilder.equals('profile', filters.profile);
    }
    if (filters.teamId && filters.teamId !== 'all') {
      queryBuilder.equals('teamId', filters.teamId);
    }
    if (filters.search) queryBuilder.search(filters.search);

    return queryBuilder.build();
  }, [filters, page, pageSize]);

  const {
    users,
    loading: usersLoading,
    error: usersError,
    refetch: refetchUsers,
  } = useUsers(query);

  const { teams, loading: teamsLoading, error: teamsError } = useTeams();

  const loading = usersLoading || teamsLoading;
  const error = usersError ?? teamsError;

  const handleInviteUser = () => {
    if (canCreateUser) {
      setIsInviteModalOpen(true);
    }
  };

  const handleFiltersChange = (newFilters: UserFiltersState) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  const headerActions = canCreateUser ? (
    <div className="flex items-center gap-4">
      <Button
        onClick={handleInviteUser}
        variant="primary"
        appearance="filled"
        size="md"
        className="flex items-center gap-2"
        disabled={loading}
      >
        <PiUserPlus className="w-4 h-4" />
        <span className="whitespace-nowrap">{t('users.actions.invite')}</span>
      </Button>
    </div>
  ) : null;
  const toolbarToggleLabel = isToolbarExpanded
    ? t('catalog.properties.toolbar.hideToolbar')
    : t('catalog.properties.toolbar.showToolbar');

  const renderPaginationEnd = () => (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2">
        <Text
          size="tiny"
          color="secondary"
          className="hidden text-sm font-normal text-secondary-600 lg:inline"
        >
          {t('users.pagination.fetch')}
        </Text>
        <Select
          value={pageSize.toString()}
          onChange={(event) => handlePageSizeChange(Number(event.target.value))}
          options={[10, 20, 50, 100].map((size) => ({
            content: size.toString(),
            value: size.toString(),
          }))}
          size="sm"
          className="!min-w-[60px]"
        />
        <Text
          size="tiny"
          color="secondary"
          className="hidden text-sm font-normal text-secondary-600 lg:inline"
        >
          {t('users.pagination.records')}
        </Text>
      </div>
      <Pagination
        currentPage={page}
        totalPages={page + (users.length === pageSize ? 1 : 0)}
        onPageChange={(newPage) => setPage(newPage)}
        size="sm"
        variant="neutral"
        appearance="ghost"
      />
      <Tooltip content={toolbarToggleLabel} size="sm">
        <Button
          type="button"
          variant="primary"
          appearance="soft"
          size="md"
          isIconOnly
          aria-label={toolbarToggleLabel}
          onClick={() => setIsToolbarExpanded((currentValue) => !currentValue)}
        >
          {isToolbarExpanded ? (
            <PiCaretUp className="h-4 w-4" />
          ) : (
            <PiCaretDown className="h-4 w-4" />
          )}
        </Button>
      </Tooltip>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <Panel className="h-full flex flex-col">
        <PanelHeader className="px-4 py-3 border-b" actions={headerActions}>
          <div className="flex items-center gap-4">
            <PiUsers className="w-8 h-8" />
            <Text as="h2" size="h2" weight="medium">
              {t('users.title')}
            </Text>
          </div>
        </PanelHeader>

        <div className="flex flex-1 flex-col overflow-hidden">
          {error ? (
            <div className="flex h-full items-center justify-center p-4">
              <ErrorState
                title={t('users.error.title')}
                description={t('users.error.description')}
                onAction={() => refetchUsers()}
                actionContent={t('users.error.retry')}
              />
            </div>
          ) : (
            <>
              <AnimatePresence initial={false}>
                {isToolbarExpanded ? (
                  <motion.div
                    key="users-toolbar"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.22, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <UserFilters
                      filters={filters}
                      teams={teams}
                      onFiltersChange={handleFiltersChange}
                      onClearFilters={handleClearFilters}
                    />
                  </motion.div>
                ) : null}
              </AnimatePresence>
              <div className="sticky top-0 z-20 border-b border-slate-100 bg-white px-4 py-1">
                {loading ? (
                  <div className="flex items-center justify-center w-full py-1">
                    <TextSkeleton className="h-8 max-w-[200px]" />
                  </div>
                ) : (
                  <PaginationBar
                    start={
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <Text
                          size="tiny"
                          color="secondary"
                          className="font-normal text-sm text-secondary-600"
                        >
                          {t('users.pagination.summary', {
                            count: `${users.length}`,
                          })}
                        </Text>
                      </div>
                    }
                    end={renderPaginationEnd()}
                    className="bg-white"
                  />
                )}
              </div>
              <div className="flex-1 overflow-auto p-4">
                <UsersTable
                  users={users}
                  teams={teams}
                  filters={filters}
                  loading={loading}
                />
              </div>
            </>
          )}
        </div>
      </Panel>
      {canCreateUser && (
        <InviteUserModal
          isOpen={isInviteModalOpen}
          teams={teams}
          onSuccess={() => setIsInviteModalOpen(false)}
          onClose={() => setIsInviteModalOpen(false)}
        />
      )}
    </div>
  );
};

export default UsersPage;
